<script lang="ts">
import { computed, defineComponent, Ref, ref } from 'vue';
import AddAddress from 'components/User/Addresses/AddAddress.vue';
import { AddressesStore } from 'stores/Addresses/Addresses.ts';
import { storeToRefs } from 'pinia';
import AddressesService from 'src/core/services/AddressesService';

export default defineComponent({
  name: 'AddressManager',

  props: {
    selection: {
      type: Boolean,
      default: false,
    },
  },

  components: { AddAddress },

  setup() {
    const addressesStore = AddressesStore();
    const { addressesList, addressesListLoading } = storeToRefs(addressesStore);

    const editAddressDetail = ref(null);

    const addAddressDialog = ref(false);
    const editAddressDialog = ref(false);

    const defaults = computed(() => addressesList.value.defaults);

    const addressesLoading = ref(false);
    const addressActionLoading: Ref<{ [addressId: number]: boolean }> = ref({});
    addressesLoading.value = true;
    addressesStore
      .getAddresses()
      .then(() => {
        addressesLoading.value = false;
      })
      .catch(() => {
        addressesLoading.value = false;
      });

    const noData = ref(false);

    return {
      addressesList,
      addressesListLoading,
      addressActionLoading,
      addAddressDialog,
      editAddressDialog,
      editAddressDetail,
      addressesStore,
      addressesLoading,
      noData,
      defaults,
    };
  },

  methods: {
    deleteAddress(addressId: number) {
      this.addressActionLoading[addressId] = true;
      return this.addressesStore
        .deleteAddress(addressId)
        .then(({ status, message }) => {
          if (status._200_) {
            this.$q.notify({
              color: 'green',
              icon: 'fa-regular fa-circle-check',
              message: 'Adres bilgisi hesabınızdan kaldırılmıştır',
            });
          } else {
            this.$q.notify({
              color: 'red',
              icon: 'fa-solid fa-circle-exclamation',
              message,
            });
          }
          this.addressActionLoading[addressId] = false;
        })
        .catch(() => {
          this.addressActionLoading[addressId] = false;
        });
    },
    setAddressToDefault(addressId: number) {
      this.addressActionLoading[addressId] = true;
      return this.addressesStore
        .teslimatOlarakAyarla(addressId)
        .then(({ status, message }) => {
          if (status._200_) {
            this.$q.notify({
              color: 'green',
              icon: 'fa-regular fa-circle-check',
              message: 'Adres teslimat adresiniz olarak ayarlanmıştır',
            });
          } else {
            this.$q.notify({
              color: 'red',
              icon: 'fa-solid fa-circle-exclamation',
              message,
            });
          }
          this.addressActionLoading[addressId] = false;
        })
        .catch(() => {
          this.addressActionLoading[addressId] = false;
        });
    },
    setAddressToBillDefault(addressId: number) {
      this.addressActionLoading[addressId] = true;
      return this.addressesStore
        .faturaOlarakAyarla(addressId)
        .then(({ status, message }) => {
          if (status._200_) {
            this.$q.notify({
              color: 'green',
              icon: 'fa-regular fa-circle-check',
              message: 'Adres fatura adresiniz olarak ayarlanmıştır',
            });
          } else {
            this.$q.notify({
              color: 'red',
              icon: 'fa-solid fa-circle-exclamation',
              message,
            });
          }
          this.addressActionLoading[addressId] = false;
        })
        .catch(() => {
          this.addressActionLoading[addressId] = false;
        });
    },
    editAddress(addressId: number) {
      /**
       * Düzenle denildiğinde id stora gönderilecek data
       * gelince düzenle açılacak ve düzenle içerisinde düzenle data
       * hazır bulunacak
       */
      AddressesService.addressDetail(addressId).then(({ status, data }) => {
        if (status._200_) {
          this.editAddressDetail = data;
          this.editAddressDialog = true;
        }
      });
    },
    addAddress() {
      this.addAddressDialog = true;
    },

    isDefaultDelivery(addressId: number) {
      return this.defaults.teslimat === addressId;
    },

    isDefaultBilling(addressId: number) {
      return this.defaults.fatura === addressId;
    },

    selectAddress(addressId: number) {
      this.$emit('select', addressId);
    },
  },

  watch: {
    addressesListLoading(val) {
      if (!val && !this.addressesList.list.length) {
        this.noData = true;
      }
    },
  },
});
</script>

<template>
  <div class="row">
    <div class="col-12 q-mb-md q-pa-sm">
      <q-btn label="Yeni Adres Ekle" icon="add" color="primary" class="text-weight-bold q-pr-lg" rounded @click="addAddress" no-caps />
    </div>
    <div v-if="addressesListLoading" class="col-12 q-pa-sm">
      <div style="width: 100%" class="text-center">
        <q-linear-progress color="primary" indeterminate class="q-mb-lg" />
        <span class="text-h6 text-weight-bold">Adresleriniz Yükleniyor</span>
      </div>
    </div>
    <div v-else ref="addressListRef" class="col-xs-12 col-md-6 q-pa-sm scroll root-container" :key="key" v-for="(address, key) in addressesList.list">
      <q-intersection transition="scale" once class="example-item">
        <q-card flat bordered class="my-card">
          <q-card-section class="bg-amber-2">
            <div class="row items-center no-wrap">
              <div class="col">
                <div class="text-h6 text-weight-bold flex items-center">
                  <span class="q-mr-md">{{ address.name }}</span>
                  <q-btn v-if="isDefaultDelivery(address.id)" size=".55rem" round unelevated color="amber-6" class="q-ma-none q-mr-sm" text-color="white">
                    <q-tooltip class="text-subtitle2 bg-primary">Varsayılan Teslimat Adresi</q-tooltip>
                    <q-icon name="fa-solid fa-location-dot" color="primary" size=".75rem"></q-icon>
                  </q-btn>
                  <q-btn v-if="isDefaultBilling(address.id)" size=".55rem" round unelevated color="amber-6" class="q-ma-none q-mr-sm" text-color="white">
                    <q-tooltip class="text-subtitle2 bg-primary">Varsayılan Fatura Adresi</q-tooltip>
                    <q-icon name="fa-solid fa-file-invoice" color="primary" size=".75rem"></q-icon>
                  </q-btn>
                </div>
                <div class="text-subtitle2">{{ address.namesurname }}</div>
              </div>

              <div class="col-auto">
                <q-btn color="grey-7" round flat icon="more_vert" :loading="addressActionLoading[address.id]">
                  <q-menu cover auto-close>
                    <q-list class="bg-amber-1">
                      <q-item clickable @click="deleteAddress(address.id)">
                        <q-item-section>
                          <q-item-label class="flex items-center text-red text-subtitle2 text-weight-bold">
                            <q-icon name="delete" size="1.3em" class="q-mr-sm" />
                            Adresi Sil
                          </q-item-label>
                        </q-item-section>
                      </q-item>
                      <q-item v-if="!isDefaultDelivery(address.id)" clickable @click="setAddressToDefault(address.id)">
                        <q-item-section>
                          <q-item-label class="flex items-center text-black text-subtitle2 text-weight-bold">
                            <q-icon name="fa-solid fa-thumbtack" size="1.3em" class="q-mr-sm" />
                            Varsayılan Olarak Ayarla
                          </q-item-label>
                        </q-item-section>
                      </q-item>
                      <q-item v-if="!isDefaultBilling(address.id)" clickable @click="setAddressToBillDefault(address.id)">
                        <q-item-section>
                          <q-item-label class="flex items-center text-black text-subtitle2 text-weight-bold">
                            <q-icon name="fa-solid fa-thumbtack" size="1.3em" class="q-mr-sm" />
                            Fatura Adresi Olarak Ayarla
                          </q-item-label>
                        </q-item-section>
                      </q-item>
                      <q-item clickable @click="editAddress(address.id)">
                        <q-item-section>
                          <q-item-label class="flex items-center text-blue text-subtitle2 text-weight-bold">
                            <q-icon name="create" size="1.3em" class="q-mr-sm" />
                            Düzenle
                          </q-item-label>
                        </q-item-section>
                      </q-item>
                    </q-list>
                  </q-menu>
                </q-btn>
              </div>
            </div>
          </q-card-section>

          <q-card-section class="bg-amber-1">
            <span class="text-grey-8">Mahalle:</span>
            {{ address.neighbourhood }}
            <br />
            <span class="text-grey-8">Adres:</span>
            {{ address.address }}
            <span v-show="address.postcode">- {{ address.postcode }},</span>
            {{ address.district }}/{{ address.city }}
            <br />
            <span class="text-grey-8">Telefon:</span>
            {{ address.telephone }}
            <br />
            <br />
            <span class="text-grey-8">Fatura Türü:</span>
            {{ address.type === 1 ? 'Bireysel' : 'Kurumsal' }}
            <br />
            <span v-if="address.type === 1">
              <span class="text-grey-8">T.C.:</span>
              {{ address.citizenshipnumber }}
            </span>
            <span v-else>
              <span class="text-grey-8">Firma Adı:</span>
              {{ address.companyname }}
              <br />
              <span class="text-grey-8">Vergi Numarası:</span>
              {{ address.taxnumber }}
              <br />
              <span class="text-grey-8">Vergi Dairesi:</span>
              {{ address.taxoffice }}
            </span>
          </q-card-section>
          <q-separator />
          <q-card-actions v-if="selection" align="right" class="bg-amber-1">
            <q-btn color="primary" rounded dense class="text-weight-bold q-mr-sm" style="padding: 0 1rem" @click="selectAddress(address.id)" no-caps> Seç </q-btn>
          </q-card-actions>
        </q-card>
      </q-intersection>
    </div>

    <div v-if="noData && !addressesList.list.length" class="col-12 q-pa-sm">Sistemde kayıtlı adresiniz bulunmuyor, hemen yeni adres ekleyebilirsiniz</div>
  </div>
  <add-address v-model="addAddressDialog" />
  <add-address v-model="editAddressDialog" :edit-detail="editAddressDetail" />
</template>

<style scoped></style>
